import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { OPEN_LOADOUT_PANEL_EVENT } from './loadout-panel';

/**
 * Spectator Overlay - Bottom-center spectator controls
 *
 * Two modes:
 * - 'enter': "ENTER SPECTATOR MODE" button shown after death
 * - 'spectating': "Spectating {name}" with prev/next arrow buttons, plus a
 *   RESPAWN button on mobile outside BTR (see renderRespawn)
 *
 * @element spectator-overlay
 */
@customElement('spectator-overlay')
export class SpectatorOverlay extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        position: absolute;
        left: 50%;
        bottom: 20px;
        margin-left: -150px;
        width: 300px;
        /* Declared here rather than inherited from the body — the component
           owns its own typography, and repeated taps on the arrows shouldn't
           start an iOS text selection, callout or grey tap flash. Safari
           still wants the -webkit- prefix, which legacy had. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
        text-align: center;
        z-index: 10;
      }

      :host(.mobile) {
        bottom: 130px;
      }

      .spectate {
        display: inline-block;
        position: relative;
        vertical-align: top;
        padding: 9px 18px 7px 18px;
        background-color: var(--color-btn-login);
        border-radius: var(--radius-sm);
        font-size: var(--font-size-16);
        font-weight: bold;
        cursor: pointer;
        pointer-events: auto;
        color: var(--color-text-white);
        text-shadow: var(--shadow-text-btn);
        border-bottom: 2px solid var(--color-border-soft);
        margin-bottom: 24px;
        touch-action: manipulation;
      }

      /* Mobile-only way back in (see renderRespawn). Sits under the arrows, the
         thumb end of the overlay; the arrows' own 20px margin spaces it. */
      .spectate.respawn {
        margin-bottom: 20px;
      }

      /* Hover rules are gated: this overlay shows on phones too, where a tap
         would leave the button or arrow stuck in its lit state. :active gives
         touch its press feedback instead. */
      @media (hover: hover) {
        .spectate:hover {
          background-color: var(--color-btn-login-hover);
          box-shadow: var(--shadow-btn-hover);
        }
      }

      .spectate:active {
        border-bottom: none;
        margin-top: 2px;
      }

      /* One line, cut with an ellipsis: the box is a fixed 30px tall, so a
         long unbroken name used to wrap onto a second line over the arrows. */
      .spectating {
        text-shadow: var(--shadow-text);
        font-size: var(--font-size-16);
        font-weight: bold;
        color: var(--color-text-white);
        text-align: center;
        height: 30px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .buttons {
        margin-bottom: 20px;
        text-align: center;
      }

      .changeplayer {
        display: inline-block;
        position: relative;
        height: 38px;
        width: 38px;
        background-color: var(--color-bg-chip);
        border-radius: 7px;
        cursor: pointer;
        pointer-events: auto;
        /* Cycling through players is rapid repeat taps — don't let iOS read
           two of them as a double-tap zoom. */
        touch-action: manipulation;
      }

      .changeplayer .arrow {
        position: absolute;
        border-right: 5px solid var(--arrow-color, var(--color-text-muted));
        border-bottom: 5px solid var(--arrow-color, var(--color-text-muted));
        width: 9px;
        height: 9px;
      }

      .changeplayer.left {
        margin-right: 12px;
      }

      .changeplayer.left .arrow {
        top: 12px;
        left: 14px;
        transform: rotate(135deg);
      }

      .changeplayer.right .arrow {
        top: 12px;
        left: 10px;
        transform: rotate(-45deg);
      }

      /* Lit state: hover on pointer devices, :active as a tap's press feedback.
         The arrow colour rides --arrow-color so each state sets one value. */
      @media (hover: hover) {
        .changeplayer:hover {
          background-color: var(--color-bg-selector-hover);
          --arrow-color: var(--color-text-white);
        }
      }

      .changeplayer:active {
        background-color: var(--color-bg-selector-hover);
        --arrow-color: var(--color-text-white);
      }

      @media only screen and (max-width: 599px) and (orientation: portrait),
        only screen and (max-height: 599px) and (orientation: landscape) {
        :host {
          transform: scale(0.8);
        }

        :host(.mobile) {
          bottom: 70px;
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  override connectedCallback() {
    super.connectedCallback();
    // Swallow all touch phases so taps on the spectator controls don't leak
    // into the game's window-level touch handlers (panel-close on touchstart,
    // key-release on touchcancel) or the touch-control layer. passive:false is
    // explicit because preventTouch calls preventDefault on the move.
    this.addEventListener('touchstart', this.stopTouch);
    this.addEventListener('touchmove', this.preventTouch, { passive: false });
    this.addEventListener('touchend', this.stopTouch);
    this.addEventListener('touchcancel', this.stopTouch);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('touchstart', this.stopTouch);
    this.removeEventListener('touchmove', this.preventTouch);
    this.removeEventListener('touchend', this.stopTouch);
    this.removeEventListener('touchcancel', this.stopTouch);
  }

  /** Reflect mobile state to the host class before render (not in updated) so
   *  the mobile bottom-offset applies on the first paint — no one-frame flash
   *  at the desktop position when the overlay first appears. */
  protected override willUpdate(): void {
    this.classList.toggle('mobile', this.uiState.isMobile);
  }

  override render() {
    const spectator = this.uiState.spectator;
    if (!spectator) return nothing;

    if (spectator.mode === 'enter') {
      return html` <div class="spectate" @click=${this.handleEnterSpectator}>ENTER SPECTATOR MODE</div> `;
    }

    return html`
      <div class="spectating">Spectating ${spectator.playerName}</div>
      <div class="buttons">
        <div class="changeplayer left" @click=${this.handlePrev}>
          <div class="arrow"></div>
        </div>
        <div class="changeplayer right" @click=${this.handleNext}>
          <div class="arrow"></div>
        </div>
      </div>
      ${this.renderRespawn()}
    `;
  }

  /** Mobile only: the way back into the game. Desktop has the sidebar planes
   *  and the 1-5 keys; a phone has neither, only the loadout chip up in the
   *  top bar, which nothing on this overlay pointed at. RESPAWN opens that
   *  panel, where picking a plane respawns you in it. Not in BTR, where the
   *  server won't respawn you until the match ends. */
  private renderRespawn() {
    const { isMobile, gameType } = this.uiState;
    if (!isMobile || gameType === 'btr') return nothing;
    return html`<div class="spectate respawn" @click=${this.handleRespawn}>RESPAWN</div>`;
  }

  /** stopPropagation is load-bearing: the click would otherwise reach the
   *  loadout panel's document-level outside-tap handler and close it the
   *  moment it opened. Plays the click, like every button tap here. */
  private handleRespawn = (e: Event) => {
    e.stopPropagation();
    this.api.playClick();
    window.dispatchEvent(new Event(OPEN_LOADOUT_PANEL_EVENT));
  };

  private handleEnterSpectator = (e: Event) => {
    e.stopPropagation();
    this.api.playClick();
    this.api.spectateNext();
  };

  private handleNext = (e: Event) => {
    e.stopPropagation();
    this.api.playClick();
    this.api.spectateNext();
  };

  private handlePrev = (e: Event) => {
    e.stopPropagation();
    this.api.playClick();
    this.api.spectatePrev();
  };

  private stopTouch = (e: TouchEvent) => {
    e.stopPropagation();
  };

  private preventTouch = (e: TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'spectator-overlay': SpectatorOverlay;
  }
}
